<template>
  <base-section
    id="gallery"
    class="bg-gallery"
  >
    <!-- 移动端：原生横向滚动 tab 栏，彻底绕开 Vuetify v-slide-group 的 touch 拦截 -->
    <div
      v-if="isMobile && !loading"
      class="native-tab-bar"
    >
      <button
        v-for="(t, i) in tabs"
        :key="i"
        class="native-tab-item"
        :class="{ 'native-tab-item--active': tab === i }"
        @click="tab = i"
      >
        {{ t }}
      </button>
    </div>
    <!-- 桌面端：Vuetify v-tabs -->
    <v-tabs
      v-if="!isMobile && !loading"
      :key="$i18n.locale"
      v-model="tab"
      centered
      show-arrows
      class="mb-12"
    >
      <v-tab
        v-for="(t, i) in tabs"
        :key="i"
        :value="i"
        class="text-capitalize"
      >
        {{ t }}
      </v-tab>
    </v-tabs>

    <v-container style="max-width: 1160px">
      <v-row v-if="loading">
        <v-col
          cols="12"
          class="text-center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="mt-4">
            {{ $t('common.loading') }}
          </p>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col
          v-for="product in filteredProducts"
          :key="product.title"
          class="gallery-card"
          cols="12"
          sm="6"
          md="4"
        >
          <v-fade-transition>
            <product-card
              v-bind="product"
              :src="product.path"
              :title="product.title"
              :item-id="product.slug || product.id"
              :clickable="product.clickable"
            />
          </v-fade-transition>
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  import productsData from '@/data/products.json'
  import { loadImage } from '@/utils/imageLoader'
  import ProductCard from '@/components/ProductCard'

  export default {
    name: 'ProductsGallery',

    components: {
      ProductCard,
    },

    data: () => ({
      tab: 0,
      products: [],
      categoryOptions: [],
      loading: true,
    }),

    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width <= 960
      },
      tabs() {
        return [this.$t('products.gallery.all'), ...this.categoryOptions.map(item => item.label)]
      },

      activeCategoryValue() {
        if (this.tab <= 0) return ''
        const option = this.categoryOptions[this.tab - 1]
        return option?.value || ''
      },

      filteredProducts() {
        const filtered = this.products.filter(product => {
          if (!this.activeCategoryValue) return true
          return product.category === this.activeCategoryValue
        })
        return filtered
      },
    },

    watch: {
      '$i18n.locale'() {
        this.loadProducts()
      },
      // 已在 Products 页时点击子目录（仅 query 变化）也能切换 tab
      '$route.query.category'(newVal) {
        if (!newVal) {
          this.tab = 0
          return
        }
        const idx = this.categoryOptions.findIndex(opt => opt.value === newVal)
        if (idx >= 0) this.tab = idx + 1
      },
      // tab 切换时同步更新 URL query 并通知标题变化
      tab(newVal) {
        const value = newVal === 0 ? undefined : (this.categoryOptions[newVal - 1]?.value)
        const currentCategory = this.$route.query.category
        // 只在值真正变化时才更新路由，避免循环触发
        if (value !== currentCategory) {
          this.$router.replace({ query: value ? { category: value } : {} }).catch(() => {})
        }
        this.notifyTitleChange()
      },
    },

    async mounted() {
      await this.loadProducts()
    },

    methods: {
      // 向 HeroAlt 和 Products/Index 广播当前激活分类的显示名称
      notifyTitleChange() {
        const isAll = this.tab === 0
        const label = isAll
          ? this.$t('products.title')
          : (this.categoryOptions[this.tab - 1]?.label || this.$t('products.title'))
        this.$root.$emit('gallery-tab-changed', { label, isAll })
      },
      translateIfKey(value) {
        if (typeof value !== 'string') return value
        return this.$te(value) ? this.$t(value) : value
      },
      slugify(text) {
        if (!text) return ''
        return text
          .toString()
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-')
      },
      async loadProducts() {
        this.loading = true
        try {
          await this.loadCategoryOptions()
          const resp = await this.$axios({
            method: 'get',
            url: '/public/products',
            params: { lang: this.$i18n.locale },
          })
          const cards = await this.buildCardsFromApi(resp)
          this.products = cards.length ? cards : await this.buildCardsFromLocal()
        } catch (error) {
          console.error('Failed to load products:', error)
          this.products = await this.buildCardsFromLocal()
        } finally {
          this.loading = false
          // 加载完成后通知标题（初始加载或语言切换后都需要）
          this.notifyTitleChange()
        }
      },
      async loadCategoryOptions() {
        const locale = (this.$i18n.locale || 'en').toLowerCase()
        let list = []
        try {
          const resp = await this.$axios({
            method: 'get',
            url: '/public/dict/data',
            params: { dictType: 'product_category' },
          })
          const active = Array.isArray(resp) ? resp.filter(item => item?.status === '0') : []
          if (active.length > 0) {
            list = active
          }
        } catch {
        }

        if (!Array.isArray(list) || list.length === 0) {
          this.categoryOptions = [
            { label: locale.startsWith('zh') ? '无人机' : 'Drone', value: 'Drone' },
            { label: locale.startsWith('zh') ? '抗干扰' : 'Anti-Jamming', value: 'Anti-Jamming' },
            { label: locale.startsWith('zh') ? '成像' : 'Imaging', value: 'Imaging' },
            { label: locale.startsWith('zh') ? '反无人机' : 'Anti-Drone', value: 'Anti-Drone' },
            { label: locale.startsWith('zh') ? '无GNSS' : 'GNSS-Free', value: 'GNSS-Free' },
          ]
        } else {
          this.categoryOptions = list.map(item => ({
            label: item.dictLabel || item.dictValue,
            value: item.dictValue,
          }))
        }

        if (this.tab >= this.tabs.length) {
          this.tab = 0
        }

        // 如果 URL query 中有 category 参数，自动激活对应 tab
        const queryCategory = this.$route && this.$route.query && this.$route.query.category
        if (queryCategory) {
          const idx = this.categoryOptions.findIndex(opt => opt.value === queryCategory)
          if (idx >= 0) this.tab = idx + 1 // +1 因为 tab[0] 是"全部"
        }
      },
      async buildCardsFromApi(products) {
        if (!Array.isArray(products) || !products.length) {
          return []
        }
        const cards = []
        const sortedProducts = [...products]
          .filter(product => product && product.visible !== false)
          .sort((a, b) => (Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)) || (Number(b?.id || 0) - Number(a?.id || 0)))
        for (const product of sortedProducts) {
          const title = this.translateIfKey(product.name)
          const slug = product.slug || String(product.id)
          const path = await loadImage(product.coverImage || require('@/assets/logo/logo.png'))
          cards.push({
            id: product.id,
            title,
            desc: product.desc || product.summary || '',
            path,
            slug,
            category: product.category,
            clickable: product.clickable !== false,
          })
        }
        return cards
      },
      async buildCardsFromLocal() {
        const localItems = Array.isArray(productsData?.products) ? productsData.products : []
        const products = []
        for (const product of localItems) {
          try {
            const slug = product.slug || String(product.id)
            const path = await loadImage(product.imgUrl)
            products.push({
              id: product.id,
              title: this.translateIfKey(product.title),
              desc: product.desc,
              path,
              slug,
              category: product.category,
              clickable: product.clickable !== false,
            })
          } catch (imgError) {
            products.push({
              id: product.id,
              title: this.translateIfKey(product.title),
              desc: product.desc,
              path: '',
              slug: product.slug || String(product.id),
              category: product.category,
              clickable: product.clickable !== false,
            })
          }
        }
        return products
      },
    },
  }
</script>

<style lang="sass">
  .bg-gallery
    background: url('~@/assets/bg-w-1.png') no-repeat center center
  .gallery-card
    transform-origin: center center 0
    transition: .3s cubic-bezier(.25,.8,.50,1)
</style>
